import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Clock, Tag, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { useNavigate } from 'react-router-dom';
import { HorizontalAd, DisplayAd } from './AdSense';
import NewsletterSignup from './NewsletterSignup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/blog/posts`);
        setPosts(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const allCategories = ['All', ...new Set(posts.flatMap(post => post.categories))];

  const filteredPosts = selectedCategory === 'All' 
    ? posts 
    : posts.filter(post => post.categories.includes(selectedCategory));

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gold-500 text-xl">Loading posts...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16">
      <Helmet>
        <title>Digital Marketing Blog 2026 - Aryan Shivhare | Strategies, SEO & Case Studies</title>
        <meta name="description" content="Actionable digital marketing insights for 2026 — paid social, Instagram growth, SEO, email automation, and AI-driven strategy. Real case studies from Berlin Fashion Week and TEDx." />
        <meta name="keywords" content="digital marketing blog, Berlin marketing, paid social, Instagram growth, SEO, AI marketing, marketing case studies 2026" />
        <link rel="canonical" href="https://aryanshivhare.com/blog" />
        <meta property="og:title" content="Digital Marketing Blog 2026 - Aryan Shivhare" />
        <meta property="og:description" content="Actionable digital marketing insights — paid social, Instagram growth, SEO, and AI-driven strategy from a Berlin performance marketer." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aryanshivhare.com/blog" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="text-center">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-gold-500 mb-6">
            Digital Marketing Insights
          </h1>
          <p className="text-base sm:text-lg text-gray-300 max-w-3xl mx-auto">
            Strategies, case studies, and actionable insights from growing brands like Berlin Fashion Week and TEDx.
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="flex flex-wrap gap-3 justify-center">
          {allCategories.map((category) => (
            <Button
              key={category}
              onClick={() => setSelectedCategory(category)}
              variant={selectedCategory === category ? "default" : "outline"}
              className={selectedCategory === category 
                ? "bg-gold-500 hover:bg-gold-600 text-black"
                : "border-gold-500/30 text-gray-300 hover:bg-gold-500/10 hover:text-gold-500"
              }
            >
              {category}
            </Button>
          ))}
        </div>
      </div>

      {/* Ad - After Category Filter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <HorizontalAd />
      </div>

      {/* Blog Posts Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {filteredPosts.length === 0 ? (
          <div className="text-center text-gray-400 py-12">
            No posts found in this category.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <React.Fragment key={post.id}>
                <Card 
                  className="bg-gray-900 border-gold-500/20 hover:border-gold-500/40 transition-all duration-300 cursor-pointer group overflow-hidden"
                  onClick={() => navigate(`/blog/${post.slug}`)}
                >
                  {/* Featured Image */}
                  {post.featured_image && (
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={post.featured_image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent" />
                    </div>
                  )}

                <CardHeader>
                  {/* Categories */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {post.categories.slice(0, 2).map((cat) => (
                      <Badge key={cat} className="bg-gold-500/20 text-gold-500 hover:bg-gold-500/30">
                        {cat}
                      </Badge>
                    ))}
                  </div>

                  <CardTitle className="text-xl font-bold text-white group-hover:text-gold-500 transition-colors line-clamp-2">
                    {post.title}
                  </CardTitle>
                  
                  <CardDescription className="text-gray-400 line-clamp-3">
                    {post.excerpt}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  {/* Meta Info */}
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(post.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{post.read_time} min read</span>
                    </div>
                  </div>

                  {/* Read More Link */}
                  <div className="flex items-center text-gold-500 group-hover:text-gold-400 transition-colors">
                    <span className="font-semibold">Read Article</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
              
              {/* Ad - After every 6 posts */}
              {(index + 1) % 6 === 0 && index !== filteredPosts.length - 1 && (
                <div className="md:col-span-2 lg:col-span-3">
                  <DisplayAd />
                </div>
              )}
            </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Ad - Before CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        <HorizontalAd />
      </div>

      {/* Newsletter Signup */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <NewsletterSignup source="blog" />
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="bg-gradient-to-r from-gold-500/10 to-gold-500/5 border border-gold-500/20 rounded-2xl p-8 md:p-12 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gold-500 mb-4">
            Want These Results for Your Brand?
          </h2>
          <p className="text-gray-300 text-lg mb-6 max-w-2xl mx-auto">
            Let's discuss how I can help grow your social media presence with proven strategies.
          </p>
          <Button 
            onClick={() => navigate('/#contact')}
            className="bg-gold-500 hover:bg-gold-600 text-black font-semibold px-8 py-6 text-lg"
          >
            Schedule a Consultation
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Blog;